import { LocalApiError, type LocalApiClient, type LocalSnapshot } from "./local-api.ts";

export type FindProgress = "sending" | "accepted" | "sent" | "replied" | "found" | "unconfirmed" | "failed" | "cancelled";
type Options = {
  isCurrent: () => boolean;
  pause: (ms: number) => Promise<void>;
  onUpdate: (snapshot: LocalSnapshot, progress: FindProgress) => void;
  maxChecks?: number;
  readSnapshot?: (deviceId: string) => Promise<LocalSnapshot>;
};

export async function followFindRequest(client: LocalApiClient, deviceId: string, options: Options) {
  const readSnapshot = options.readSnapshot ?? ((id: string) => client.snapshot(id));
  const check = () => { if (!options.isCurrent()) throw new LocalApiError("cancelled"); };
  const baseline = await readSnapshot(deviceId);
  check();
  if (!baseline.device?.connected) throw new LocalApiError("deviceOffline");
  if (!baseline.device.capabilities?.findTracker) throw new LocalApiError("featureUnavailable");
  const { commandId } = await client.findTracker(deviceId);
  check();
  options.onUpdate(baseline, "accepted");
  let latest = baseline;
  for (let i = 0; i < (options.maxChecks ?? 10); i++) {
    await options.pause(3000);
    check();
    latest = await readSnapshot(deviceId);
    check();
    const command = latest.device?.findCommand;
    if (command?.id === commandId) {
      if (command.repliedAt) { options.onUpdate(latest, "replied"); return; }
      if (command.status === "failed") { options.onUpdate(latest, "failed"); return; }
      options.onUpdate(latest, command.sentAt ? "sent" : "accepted");
    }
    if (!latest.device?.connected) break;
  }
  options.onUpdate(latest, "unconfirmed");
}
